import { api } from '../utils/api';

/**
 * Service for managing transcript analysis
 */
class TranscriptAnalysisService {
  /**
   * Generate transcript analysis for a jobseeker
   * @param {string} jobseekerId - The jobseeker's ID
   * @param {string} companyId - The company's ID (optional if using auth token)
   * @returns {Promise<Object>} Analysis report
   */
  async generateAnalysis(jobseekerId, companyId = null) {
    try {
      const response = await api.post(
        `/api/transcript-analysis/generate/${jobseekerId}`,
        companyId ? { companyId } : {}
      );
      return response.data;
    } catch (error) {
      console.error('Error generating transcript analysis:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get existing transcript analysis for a jobseeker
   * @param {string} jobseekerId - The jobseeker's ID
   * @param {string} companyId - The company's ID (optional if using auth token)
   * @returns {Promise<Object>} Analysis report
   */
  async getAnalysis(jobseekerId, companyId = null) {
    try {
      const params = companyId ? { companyId } : {};
      const response = await api.get(`/api/transcript-analysis/${jobseekerId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting transcript analysis:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get transcript analysis status for a jobseeker
   * @param {string} jobseekerId - The jobseeker's ID
   * @param {string} companyId - The company's ID (optional if using auth token)
   * @returns {Promise<Object>} Status information
   */
  async getStatus(jobseekerId, companyId = null) {
    try {
      const params = companyId ? { companyId } : {};
      const response = await api.get(`/api/transcript-analysis/status/${jobseekerId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting transcript analysis status:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Regenerate transcript analysis for a jobseeker
   * @param {string} jobseekerId - The jobseeker's ID
   * @param {string} companyId - The company's ID (optional if using auth token)
   * @returns {Promise<Object>} Analysis report
   */
  async regenerateAnalysis(jobseekerId, companyId = null) {
    try {
      const response = await api.put(
        `/api/transcript-analysis/regenerate/${jobseekerId}`,
        companyId ? { companyId } : {}
      );
      return response.data;
    } catch (error) {
      console.error('Error regenerating transcript analysis:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - The error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
      const err = new Error(message);
      err.status = error.response.status;
      err.data = error.response.data;
      return err;
    } else if (error.request) {
      // The request was made but no response was received
      return new Error('No response from server. Please check your connection.');
    } else {
      // Something happened in setting up the request that triggered an Error
      return error;
    }
  }
}

// Export singleton instance
export default new TranscriptAnalysisService();
